import { useEffect, useState } from "react";

import { useDashboardActions } from "../../../lib/useDashboardActions";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { useDashboard } from "../../../lib/useDashboard";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { usePlan } from "@/entities/Plan";
import { generateRandomHash } from "@/shared/lib/hash";
import { getInitialBoolean, getInitialNumber } from "@/shared/lib/localStorage";

export interface TopUsersInterface{
  id: number;
  photoUrl: string;
  place: number;
  percent: number;
  name: string;
  fullName: string;
}

const useTopEmployeesCompany = () => {
  const { isAuth, user } = useAuth();
  const { users } = useWorkspace();
  const { planTypes, loading: planLoading } = usePlan();

  const { loading: dashboardLoading, error, dateStart, dateEnd, dashboardTopEmployeesCompany: emps, setDashboardTopEmployeesCompany } = useDashboard();
  const { getTopEmployees } = useDashboardActions();

  // --- localStorage keys ---
  const LS_IS_PERIOD_KEY = 'topEmployeesCompany_isPeriod';
  const LS_TYPE_ID_KEY = 'topEmployeesCompany_typeId';

  const [isPeriod, setIsPeriodState] = useState<boolean>(getInitialBoolean(LS_IS_PERIOD_KEY, false));
  const [typeId, setTypeIdState] = useState<number>(getInitialNumber(LS_TYPE_ID_KEY,4));
  const [compEmp, setCompEmp] = useState<TopUsersInterface[]>([]);

  // Получение данных
  useEffect(() => {
    if (isAuth && dateStart && dateEnd) {
      getTopEmployees({
        dateStart,
        dateEnd,
        isPeriod,
        typeId,
      }).then((result) => {
        setDashboardTopEmployeesCompany(result);
      });
    }
    // eslint-disable-next-line
  }, [isAuth, dateStart, dateEnd, isPeriod, typeId]);

  // --- эффекты для записи в localStorage ---
  useEffect(() => {
    localStorage.setItem(LS_IS_PERIOD_KEY, String(isPeriod));
  }, [isPeriod]);
  useEffect(() => {
    localStorage.setItem(LS_TYPE_ID_KEY, String(typeId));
  }, [typeId]);

  // Сообщение при отсутствии данных
  let fullMessage: string | undefined = undefined;
  if (!dashboardLoading.dashboardTopEmployees && emps.length === 0) {
    const planObject = planTypes.find((pt) => pt.id === typeId)?.plan_object;
    if (planObject) {
      fullMessage = `Нет планов "${planObject}" или сотрудников`;
    } else {
      fullMessage = "Нет планов, продаж или сотрудников";
    }
  }

  // Формируем топ 5 для карточки
  useEffect(() => {
    if (emps.length > 0) {
      const us = emps
        .map((u) => {
          const userData = users.find((_u) => _u.id === u.id);
          return {
            ...u,
            name: `${userData?.name || ""}`,
            fullName: `${userData?.name || ""} ${userData?.last_name || ""}`,
            photoUrl: userData?.personal_photo || "",
          } as TopUsersInterface;
        });
      setCompEmp(us);
    }
    // eslint-disable-next-line
  }, [users, emps]);

  let { top, current } = getDisplayEmployees(compEmp, user?.bx.bx_user.bx_id);

  const emptyCount = 5 - top.length - (current ? 1 : 0);
  if (emptyCount > 0 ) {
    top = [
      ...top,
      ...Array.from({ length: emptyCount }, (_, i) => ({
        id: -(i + 1),
        name: '',
        fullName: '',
        photoUrl: `https://gravatar.com/avatar/${generateRandomHash()}?s=400&d=monsterid&r=x`,
        place: top.length + i + 1,
        percent: 0,
        isEmpty: true
      }))
    ];
  }

  // --- экспортируемые сеттеры ---
  return {
    top5: top,
    top: compEmp,
    loading: dashboardLoading.dashboardTopEmployees || planLoading.planTypes,
    error: error.dashboardTopEmployees,
    fullMessage,
    current: current ? current : null,
    currentUserId: user?.bx.bx_user.bx_id,

    // for popup
    planTypes,
    isPeriod,
    setIsPeriod: setIsPeriodState,
    typeId,
    setTypeId: setTypeIdState,
  }
}

export function getDisplayEmployees(
  employees: TopUsersInterface[] | null, 
  currentUserId: number | undefined
) {
  if (!employees) return { top: [], current: null };
  
  const top5 = employees.slice(0, 5);
  const currentIdx = employees.findIndex(e => e.id === currentUserId);

  if (currentIdx >= 0 && currentIdx < 5) {
    return { top: top5 };
  } else if (currentIdx >= 0) {
    const top4 = employees.slice(0, 4);
    return { top: top4, current: employees[currentIdx] };
  } else {
    return { top: top5 };
  }
}

export default useTopEmployeesCompany;